import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FAQ, SITE } from "@/lib/site";
import { useProof } from "@/lib/proof";
import { Container, Section, SectionHeader, Reveal, CTAButton, Logo } from "@/components/ui-kit";
import { cn } from "@/lib/utils";

const initialOf = (t) => t.initial || (t.name || "?").trim()[0]?.toUpperCase();

export const Testimonials = () => {
  const [i, setI] = useState(0);
  const { items: TESTIMONIALS, mode } = useProof("testimonials");
  if (mode === "hidden" || !TESTIMONIALS.length) return null;
  const t = TESTIMONIALS[Math.min(i, TESTIMONIALS.length - 1)];
  return (
    <Section data-testid="testimonials-section">
      <Container>
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 min-w-0">
            <SectionHeader eyebrow="The verdict" title="Operators love how much faster they move." />
            <div className="mt-8 flex flex-wrap gap-2">
              {TESTIMONIALS.map((x, idx) => (
                <button key={`${x.name}-${idx}`} type="button" onClick={() => setI(idx)} data-testid={`testimonial-tab-${idx}`} className={cn("flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors", idx === i ? "border-mk-lime bg-mk-lime/10 text-mk-text" : "border-mk-border text-mk-dim hover:text-mk-text")}>
                  <span className={cn("grid place-items-center h-5 w-5 rounded-full text-[10px] font-bold", idx === i ? "bg-mk-lime text-mk-bg" : "bg-mk-raised")}>{initialOf(x)}</span>{x.company}
                </button>
              ))}
            </div>
          </div>
          <Reveal className="lg:col-span-7 min-w-0">
            <div key={i} className="relative rounded-3xl border border-mk-border bg-mk-surface p-8 sm:p-10 animate-fade-in-up" data-testid="testimonial-card">
              <Quote className="absolute top-6 right-6 h-10 w-10 text-mk-lime/20" />
              <p className="font-display text-2xl sm:text-3xl font-medium leading-snug text-mk-text text-balance">“{t.quote}”</p>
              <div className="mt-8 flex items-center gap-4">
                {t.avatar_url ? <img src={t.avatar_url} alt={t.name} className="h-12 w-12 rounded-full object-cover" /> : <span className="grid place-items-center h-12 w-12 rounded-full bg-mk-lime text-mk-bg font-display font-bold text-lg">{initialOf(t)}</span>}
                <div><div className="font-semibold text-mk-text">{t.name}</div><div className="text-sm text-mk-dim">{[t.role, t.company].filter(Boolean).join(", ")}</div></div>
                {t.metric && <span className="ml-auto rounded-full border border-mk-lime/40 bg-mk-lime/10 px-3 py-1 font-mono text-xs text-mk-lime">{t.metric}</span>}
              </div>
            </div>
          </Reveal>
        </div>
      </Container>
    </Section>
  );
};

export const CaseStudies = () => {
  const { items: CASE_STUDIES, mode } = useProof("case_studies");
  if (mode === "hidden" || !CASE_STUDIES.length) return null;
  return (
  <Section className="bg-mk-surface/40 border-y border-mk-border" data-testid="case-studies-section">
    <Container>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <SectionHeader eyebrow="Case studies" title="Real results from real operators." />
        <Link to="/resources" data-testid="case-studies-all" className="btn-ghost self-start">All stories <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="mt-12 grid md:grid-cols-3 gap-5">
        {CASE_STUDIES.map((c, i) => (
          <Reveal key={`${c.title}-${i}`} delay={i * 0.08}>
            <Link to={c.url || "/resources"} data-testid={`case-study-${i}`} className="card-dark group block h-full">
              <div className="relative h-48 overflow-hidden">{c.image ? <img src={c.image} alt={c.title} loading="lazy" className="h-full w-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-[opacity,transform] duration-700" /> : <div className="h-full w-full bg-mk-raised dot-grid" />}<div className="absolute inset-0 bg-gradient-to-t from-mk-surface to-transparent" />{c.metric && <span className="absolute top-4 left-4 rounded-full bg-mk-lime px-3 py-1 font-mono text-[10px] font-bold text-mk-bg">{c.metric}</span>}</div>
              <div className="p-6"><div className="font-mono text-[10px] uppercase tracking-wider text-mk-muted">{c.industry}</div><h3 className="mt-2 font-display text-lg font-semibold text-mk-text group-hover:text-mk-lime transition-colors">{c.title}</h3><p className="mt-2 text-sm text-mk-dim leading-relaxed">{c.excerpt}</p><span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-mk-text">Read story <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span></div>
            </Link>
          </Reveal>
        ))}
      </div>
    </Container>
  </Section>
  );
};

export const FAQSection = ({ items = FAQ, title = "Questions? We've got answers.", eyebrow = "FAQ" }) => (
  <Section data-testid="faq-section">
    <Container>
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4 min-w-0"><SectionHeader eyebrow={eyebrow} title={title} desc="Still unsure? The Fixlr Assistant in the corner knows every module and every price — or book a 20-minute demo." /><div className="mt-6"><CTAButton to="/contact" variant="ghost" testId="faq-contact-btn">Book a demo</CTAButton></div></div>
        <div className="lg:col-span-8 min-w-0">
          <Accordion type="single" collapsible className="divide-y divide-mk-border rounded-2xl border border-mk-border bg-mk-surface px-2">
            {items.map((f, i) => (
              <AccordionItem key={f.q} value={`q-${i}`} className="border-0 px-4">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-base font-medium text-mk-text hover:text-mk-lime hover:no-underline py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-mk-dim leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </Container>
  </Section>
);

export const CTABand = ({ title = "Run maintenance like a platform company.", desc = "7-day free trial. No card required. Migrate your existing jobs and suppliers in one afternoon." }) => (
  <Section className="pb-24" data-testid="cta-band">
    <Container>
      <Reveal className="relative overflow-hidden rounded-3xl border border-mk-border bg-mk-surface p-10 sm:p-16 text-center beam">
        <div className="absolute inset-0 bg-grid-dark opacity-40" aria-hidden />
        <div className="orb h-[400px] w-[400px] -top-40 left-1/2 -translate-x-1/2 bg-mk-lime/20" aria-hidden />
        <div className="relative">
          <Logo size="h-12" className="justify-center" />
          <div className="mt-3 eyebrow">Ready when you are</div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-mk-text text-balance">{title}</h2>
          <p className="mt-4 text-base md:text-lg text-mk-dim max-w-2xl mx-auto">{desc}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <CTAButton href={SITE.signupUrl} testId="cta-band-trial-btn">Start free trial <ArrowRight className="h-4 w-4" /></CTAButton>
            <CTAButton to="/contact" variant="ghost" testId="cta-band-demo-btn">Book a demo</CTAButton>
          </div>
        </div>
      </Reveal>
    </Container>
  </Section>
);
